import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FontDownloadOutlinedIcon from "@mui/icons-material/FontDownloadOutlined";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import PhotoSizeSelectActualOutlinedIcon from "@mui/icons-material/PhotoSizeSelectActualOutlined";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";
import SettingsApplicationsOutlinedIcon from "@mui/icons-material/SettingsApplicationsOutlined";
import SubtitlesOutlinedIcon from "@mui/icons-material/SubtitlesOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import InventoryIcon from "@mui/icons-material/Inventory";
import EmergencyRecordingIcon from "@mui/icons-material/EmergencyRecording";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import CollectionsIcon from "@mui/icons-material/Collections";
import SourceIcon from "@mui/icons-material/Source";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AlbumIcon from "@mui/icons-material/Album";
import { Box, Stack, SvgIconProps, useTheme } from "@mui/material";
import React from "react";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { AssetTypeAlphaColor } from "../../../Utils/helpers";
import { ContentCutOutlined, LoginOutlined } from "@mui/icons-material";

export interface AssetIconProps extends SvgIconProps {
  type: ObjectType;
  colored?: boolean;
  contained?: boolean;
  size?: "small" | "medium" | "large";
}

const AssetIcon: React.FC<AssetIconProps> = ({ type, colored, ...otherProps }) => {
  const { htmlColor = undefined, contained, size = "small", ...otherOtherProps } = otherProps;
  const theme = useTheme();
  otherProps.htmlColor = colored && !contained ? (htmlColor ? htmlColor : AssetTypeAlphaColor(type, 1)) : "";

  const backgroundColor = `linear-gradient(225.52deg, ${AssetTypeAlphaColor(type, 0.8)} 0%, ${AssetTypeAlphaColor(
    type,
    0.4
  )} 80.71%, ${AssetTypeAlphaColor(type, 0.3)} 100%)`;

  const icon = () => {
    switch (type) {
      case ObjectType.Archive:
        return <Inventory2OutlinedIcon fontSize={size} />;
      case ObjectType.Audio:
      case ObjectType.AudioClip:
        return <EqualizerOutlinedIcon fontSize={size} />;

      case ObjectType.Document:
        return <ArticleOutlinedIcon fontSize={size} />;

      case ObjectType.Folder:
        return <FolderOutlinedIcon fontSize={size} />;
      case ObjectType.Font:
        return <FontDownloadOutlinedIcon fontSize={size} />;

      case ObjectType.GraphSkeleton:
        return <AccountTreeOutlinedIcon fontSize={size} />;

      case ObjectType.Image:
        return <PhotoSizeSelectActualOutlinedIcon fontSize={size} />;
      case ObjectType.ImageSequence:
        return <CollectionsIcon fontSize={size} />;

      case ObjectType.MetadataDcpKdm:
      case ObjectType.MetadataImfKdm:
      case ObjectType.KdmContainer:
        return <LockOpenOutlinedIcon fontSize={size} />;

      case ObjectType.Metadata:
      case ObjectType.MetadataDcpAssetMap:
      case ObjectType.MetadataDcpCpl:
      case ObjectType.MetadataDcpPkl:
      case ObjectType.MetadataDcpVolIndex:
      case ObjectType.MetadataHdr10:
      case ObjectType.MetadataHdrDolbyVision:
      case ObjectType.MetadataImfAssetMap:
      case ObjectType.MetadataImfCpl:
      case ObjectType.MetadataImfOpl:
      case ObjectType.MetadataImfPkl:
      case ObjectType.MetadataImfVolIndex:
      case ObjectType.MetadataProtoolsSession:
        return <SettingsApplicationsOutlinedIcon fontSize={size} />;

      case ObjectType.ScribeSubtitleProject:
        return <SubtitlesOutlinedIcon fontSize={size} />;
      case ObjectType.Subtitle:
        return <ClosedCaptionOutlinedIcon fontSize={size} />;
      case ObjectType.Video:
        return <TvOutlinedIcon fontSize={size} />;
      case ObjectType.VideoClip:
        return <ContentCutOutlined fontSize={size} />;
      case ObjectType.Unknown:
        return <QuestionMarkOutlinedIcon fontSize={size} />;

      case ObjectType.Folder:
        return <FolderOpenIcon fontSize={size} />;

      case ObjectType.Others:
      case ObjectType.BundleComponent:
      case ObjectType.BundlePackage:
      case ObjectType.DashPackage:
      case ObjectType.DolbyAtmosData:
      case ObjectType.DolbyAtmosPackage:
        return <InventoryIcon fontSize={size} />;
      case ObjectType.DcpPackage:
        return <EmergencyRecordingIcon fontSize={size} />;
      case ObjectType.Generic:
        return <SourceIcon fontSize={size} />;
      case ObjectType.Group:
      case ObjectType.GroupMasterDcp:
      case ObjectType.GroupMasterImf:
        return <GroupWorkIcon fontSize={size} />;
      case ObjectType.ImfPackage:
      case ObjectType.TimetoolSequence:
        return <MiscellaneousServicesOutlinedIcon fontSize={size} />;
      case ObjectType.UploadContainer:
        return <CloudUploadIcon fontSize={size} />;
      case ObjectType.PhysicalItem:
        return <AlbumIcon fontSize={size} />;
      case ObjectType.VirtualHead:
        return <LoginOutlined fontSize={size} sx={{ opacity: 0.7 }} />;
      default:
        return <VideocamOutlinedIcon fontSize={size} />;
    }
  };

  return contained ? (
    <Box
      sx={{
        p: size === "small" ? 0.25 : size === "medium" ? 0.5 : 1,
        position: "relative",
        background: theme.palette.background.paper,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: size === "small" ? "4px" : size === "medium" ? "6px" : "8px",
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: backgroundColor,
          borderRadius: size === "small" ? "4px" : size === "medium" ? "6px" : "8px",
        },
        ...otherProps.sx,
      }}
    >
      {icon()}
    </Box>
  ) : (
    React.cloneElement(icon(), { ...otherProps })
  );
};

export default AssetIcon;
